/**
 * THE printable LIST document — A4 LANDSCAPE, one row per record, a navy header
 * row that repeats on every page, a scope sentence that says the list ignores
 * on-screen paging — as a pure pdfmake document definition.
 *
 * 🔀 EXTRACTED 2026-09-27 out of `utils/orderListPdf.js` (verbatim: the geometry,
 * the house style, the cell helpers, the table, the scope / filter /
 * reconciliation lines and the page wrapper), when the hires pages needed the
 * SAME `Print all` / `Download list (PDF)` for hire lines (Code Unification
 * Principle — never a second copy). The COLUMNS and the NOUN are the caller's:
 * `orderListPdf.js` passes `ORDER_LIST_COLUMNS` + "order"/"orders" + subject
 * `Order List`, and prints byte-for-byte what it printed before — its own suite,
 * `orderListPdf.test.js`, is unchanged and is the proof. `hireListPdf.js` passes
 * the hire columns. Read `orderListPdf.js`'s docblock for why this is a second
 * FILE from `orderPdf.js` (portrait, single order, for a supplier) and never an
 * orientation flag on it.
 *
 * A column is `{ key, label, width, align, cell(row) → pdfmake cell }`. 🔑 Header
 * cells AND body cells are both generated from the one array, so they cannot
 * desync (the hand-ordered-`<td>` defect `components/OrderTableView.js` carries).
 *
 * No dependency on pdfmake itself — declarative doc-def data only.
 */

import { EM_DASH } from './moneyOrAbsent';
import { formatDateCompact } from './dateFormat';

const NAVY = '#1e3a5f';
const ZEBRA = '#f8f8f8';
export const SECONDARY_FG = '#666666';
const GREY_RULE = '#dddddd';
const FOOTER_GREY = '#999999';

/**
 * Page geometry, written as the arithmetic rather than as a magic number —
 * `orderPdf.js` does the same for its portrait 523, and the reason is that the
 * next person to change a margin must be able to see what depends on it.
 *
 * A4 landscape is 842 × 595pt. Printable width = 842 − (36 + 36) = **770pt**.
 */
const A4_LANDSCAPE_WIDTH = 842;
const PAGE_MARGIN_X = 36;
const PAGE_MARGIN_TOP = 36;
const PAGE_MARGIN_BOTTOM = 44;
export const CONTENT_WIDTH = A4_LANDSCAPE_WIDTH - PAGE_MARGIN_X * 2; // 770
const HEADER_LINE_WIDTH = CONTENT_WIDTH;

// ONE miss glyph for the whole document: `EM_DASH`, imported from
// `./moneyOrAbsent` (the indexed shared module) rather than declared here.
// 🪤 `formatDateCompact` returns the ASCII `'-'` as its own documented miss
// value, so a date that is simply absent would otherwise print a different
// "nothing here" mark from an absent supplier. `dateText` below normalises it.

// ── Small pure helpers ──────────────────────────────────────────────────────

/**
 * A number, or `null` when the value is not one.
 *
 * 🪤 **Type-gated BEFORE `Number()`, because `Number([])` is `0`** — the same
 * trap `server/helpers/leadTimeHelpers.js`'s `normaliseLeadTimeDays` is written
 * around. Without the gate, an order carrying `order_total: []` would print
 * `£0.00`, which is a false statement rather than a missing one.
 *
 * Strings are accepted deliberately: PostgreSQL's `pg` driver returns `NUMERIC`
 * and `COUNT(*)` as STRINGS, so `item_count` and `order_total` reach the client
 * as `'4'` / `'125.00'` in production while SQLite hands back numbers in dev.
 */
export function numeric(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Capitalise the first character — `'submitted'` → `'Submitted'` — for a
 * status enum with no dedicated label. `''` for a falsy input.
 *
 * 🔀 **EXTRACTED 2026-09-28** — `orderListPdf.js` and `orderPdf.js` each
 * carried a byte-identical private copy; `utils/orderListExport.js` needing a
 * third is what `npm run check-duplication` caught. Import it rather than
 * writing a fourth.
 */
export function cap(s) {
  return s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '';
}

/**
 * Any cell value → printable text. **Nothing may reach a cell as `undefined` or
 * `null`**: `String(undefined)` is the literal `'undefined'`, and a printed page
 * carrying that word is worse than one carrying a dash, because a reader cannot
 * tell it from data.
 */
export function text(value) {
  if (value == null) return EM_DASH;
  const s = String(value).trim();
  return s === '' ? EM_DASH : s;
}

/**
 * A date cell. `formatDateCompact` gives "25 Apr 26" — the two-digit year is the
 * saving, and it is safe here because every date in these documents is
 * DATE-ONLY.
 *
 * ⚠️ It runs its input through the shared `parseDate`, which does
 * `String(dateStr).split('T')[0]` and so DISCARDS any time component. That is
 * invisible from its signature; if a column ever needs an hour, reach for
 * `formatDateTimeCompact`, never for options on this one.
 */
export function dateText(value) {
  if (value == null || String(value).trim() === '') return EM_DASH;
  const formatted = formatDateCompact(value);
  // `formatDateCompact` returns '-' for null/invalid; fold its miss onto ours.
  return formatted === '-' ? EM_DASH : formatted;
}

/** One body cell. */
export const td = (value, alignment = 'left', extra = {}) => ({
  text: value, fontSize: 8, alignment, ...extra,
});

// ── Table ───────────────────────────────────────────────────────────────────

const headerCell = (label, alignment) => ({
  text: label, fillColor: NAVY, color: 'white', bold: true, fontSize: 8, alignment,
});

/**
 * The list table. `headerRows: 1` so the navy heading repeats on every page —
 * a landscape list is expected to run to several pages and a table whose
 * headings appear once is unreadable from page two onwards.
 */
function listTable(rows, columns, noun) {
  const header = columns.map(c => headerCell(c.label, c.align));
  const body = [header];

  if (!rows.length) {
    // Mirrors `orderPdf.js`'s empty-items handling: ONE spanning row that says
    // so, never an empty table, which reads as a failed render.
    body.push([
      {
        text: `No ${noun.many} match the current filters.`,
        colSpan: columns.length,
        alignment: 'center',
        color: '#999',
        fontSize: 8,
      },
      ...columns.slice(1).map(() => ({})),
    ]);
  } else {
    rows.forEach((row) => {
      // 🔑 ONE ROW PER RECORD. Never `flatMap` children into it — that is a
      // different document, and it silently breaks every count on the page.
      body.push(columns.map(c => c.cell(row)));
    });
  }

  return {
    table: {
      headerRows: 1,
      widths: columns.map(c => c.width),
      body,
      dontBreakRows: true,
    },
    layout: {
      fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 0 ? ZEBRA : null),
      hLineColor: () => GREY_RULE,
      vLineColor: () => GREY_RULE,
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      paddingTop: () => 3,
      paddingBottom: () => 3,
      paddingLeft: () => 4,
      paddingRight: () => 4,
    },
    margin: [0, 0, 0, 8],
  };
}

// ── Header block ────────────────────────────────────────────────────────────

/**
 * The scope sentence.
 *
 * 🔑 **THE DOCUMENT MUST STATE THAT IT LISTS EVERY MATCHING ROW, IGNORING THE
 * ON-SCREEN PAGINATION.** Direct product-owner instruction, and the reasoning is
 * the part to keep: a printed page is detached from the screen that produced it,
 * so it cannot be allowed to mislead about its own scope. Someone holding page 1
 * of 6 has no way to tell "everything that matched" from "the 25 rows that
 * happened to be on screen" unless the page says which it is.
 *
 * 🔑 **The number quoted is the number of rows ACTUALLY IN THIS DOCUMENT**, not
 * a caller-supplied figure. A caller's `totalCount` is a claim about a filter
 * result; `rows.length` is a fact about the paper. If they disagree the
 * document says so (see `reconciliationLine`) rather than repeating the claim.
 */
function scopeLine(count, noun) {
  if (count === 0) return `No ${noun.many} match the current filters (on-screen paging ignored).`;
  const word = count === 1 ? noun.one : noun.many;
  return `All ${count} ${word} matching the current filters (on-screen paging ignored).`;
}

/**
 * ⚠️ **An absent filter summary is stated, never omitted.** A missing line is
 * indistinguishable from a line nobody read; "No filters applied" is a claim the
 * reader can check against the count beside it.
 */
function filterLine(filterSummary) {
  const summary = filterSummary == null ? '' : String(filterSummary).trim();
  return summary === '' ? 'No filters applied.' : `Filters: ${summary}`;
}

/**
 * The honesty valve for a caller-supplied `totalCount` that disagrees with the
 * rows handed over — the scope sentence promises everything matching is here,
 * so if the caller's own figure says otherwise the document says so on its face.
 * Returns `null` when they agree, which is the ordinary case.
 */
function reconciliationLine(totalCount, rendered, noun) {
  const claimed = numeric(totalCount);
  if (claimed == null || claimed === rendered) return null;
  return `Note: the caller reported ${claimed} matching ${noun.many}; ${rendered} ${rendered === 1 ? 'is' : 'are'} listed here.`;
}

// ── Inner content (no page wrapper) ─────────────────────────────────────────

/**
 * Just the pdfmake `content[]` slice for the list. Pure + testable, exported so
 * the wrapper and the content are separately assertable.
 *
 * `showScopeLine` (default `true` — every list page, byte-identical) — `false`
 * drops the "All N … matching the current filters (on-screen paging ignored)"
 * sentence. 🔀 2026-10-28 (R2 F6): the timesheet EXPORT is not a filtered
 * on-screen list, so that sentence was false of it ("All 3 sub-departments
 * matching the current filters"); it states its own scope instead.
 */
export function buildListContent({
  rows = [],
  columns,
  noun,
  headerTitle,
  departmentName = '',
  filterSummary = '',
  totalCount = null,
  showScopeLine = true,
}) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const reconcile = reconciliationLine(totalCount, list.length, noun);

  const content = [
    { text: text(headerTitle), fontSize: 18, bold: true, color: NAVY },
    departmentName && {
      text: String(departmentName), fontSize: 12, bold: true, color: '#333', margin: [0, 2, 0, 0],
    },
    showScopeLine && { text: scopeLine(list.length, noun), fontSize: 9, color: SECONDARY_FG, margin: [0, 3, 0, 0] },
    { text: filterLine(filterSummary), fontSize: 9, color: SECONDARY_FG, margin: [0, 1, 0, 0] },
    reconcile && { text: reconcile, fontSize: 9, italics: true, color: SECONDARY_FG, margin: [0, 1, 0, 0] },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: HEADER_LINE_WIDTH, y2: 0, lineWidth: 2, lineColor: NAVY }],
      margin: [0, 6, 0, 10],
    },
    listTable(list, columns, noun),
  ];

  return content.filter(Boolean);
}

// ── Full doc wrapper ────────────────────────────────────────────────────────

/** A `Date` from whatever the caller passed, falling back to now. */
function resolveGeneratedAt(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value != null && value !== '') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

const footerDate = (at) => resolveGeneratedAt(at).toLocaleDateString('en-GB', {
  day: '2-digit', month: 'long', year: 'numeric',
});

/**
 * @param {object} args  `buildListContent`'s args plus `subject` (the PDF
 *   `info.subject`) and `generatedAt` (the footer date; defaults to now).
 */
export function buildListDoc(args) {
  const { headerTitle, departmentName = '', generatedAt = null, subject } = args;
  const title = text(headerTitle);

  return {
    pageSize: 'A4',
    /**
     * 🪤 **THE ORIENTATION OF A pdfmake DOCUMENT COMES FROM THIS LINE AND
     * NOWHERE ELSE.**
     *
     * `hooks/useSharePdf.js` takes an `orientation` option, and it is tempting —
     * and wrong — to think a caller must pass `orientation: 'landscape'` to get
     * a landscape PDF out of this builder. That option feeds ONLY the html2pdf
     * (raster) path: the off-screen container width and the `jsPDF` config. The
     * `renderPdfMakeDoc` path never reads it, so on this document it is
     * **inert**. `components/OrderPdfMenuItems.js` passes
     * `orientation: 'portrait'` alongside `renderPdfMakeDoc` and that line does
     * nothing at all — `orderPdf.js` is portrait because its own doc def says
     * so. Deleting the line below would silently revert this list to portrait
     * with its columns crushed into 523pt.
     */
    pageOrientation: 'landscape',
    pageMargins: [PAGE_MARGIN_X, PAGE_MARGIN_TOP, PAGE_MARGIN_X, PAGE_MARGIN_BOTTOM],
    info: {
      title,
      author: departmentName ? String(departmentName) : 'Crew Forge',
      subject,
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${footerDate(generatedAt)} | ${title} | Crew Forge`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'left',
          margin: [PAGE_MARGIN_X, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7, color: FOOTER_GREY, alignment: 'right',
          margin: [0, 8, PAGE_MARGIN_X, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000' },
    content: buildListContent(args),
  };
}
